import { type DataProvider, HttpError } from 'react-admin';
import { fetchUtils } from 'react-admin';
import type LogtoClient from "@logto/browser";
import type { BuildReadiness, Metrics, TemplateSecretStatus } from "./types";

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// HTTP client with bearer token authentication
export const httpClient = (client: LogtoClient) => async (
    url: any,
    options: fetchUtils.Options | undefined
) => {
    let token: string | null;
    try {
        // Auto-refreshes via the refresh token when the access token is expired.
        // Throws if refresh fails (no/invalid refresh token) — surface as 401 so
        // authProvider.checkError can trigger re-authentication instead of leaving
        // the page blank.
        token = await client.getAccessToken(apiUrl);
    } catch (error: any) {
        throw new HttpError(error?.message || 'Session expired', 401, error);
    }
    const requestHeaders = getHeaders(token, options);
    return fetchUtils.fetchJson(url, {
        ...options,
        headers: requestHeaders,
    }).catch(error => {
        // Keep the whole body: 422 responses carry per-field errors in `fields`.
        throw new HttpError(error.body?.message || error.message, error.status, error.body);
    });
};

export const getHeaders = (
    token: string | null,
    options: fetchUtils.Options | undefined
): Headers => {
    const headers = ((options && options.headers) ||
        new Headers({
            Accept: 'application/json',
        })) as Headers;
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
};

// Transform snake_case to camelCase
const toCamelCase = (str: string): string =>
    str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

// Transform object keys from snake_case to camelCase
const transformKeys = (obj: any, preserveValues = false): any => {
    if (preserveValues) return obj;
    if (Array.isArray(obj)) {
        return obj.map(v => transformKeys(v));
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc: any, key: string) => {
            const camelKey = toCamelCase(key);
            const shouldPreserve = preserveNestedKeys.has(key) || preserveNestedKeys.has(camelKey);
            acc[camelKey] = shouldPreserve ? obj[key] : transformKeys(obj[key]);
            return acc;
        }, {});
    }
    return obj;
};

// Transform camelCase to snake_case
const toSnakeCase = (str: string): string =>
    str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

// Keys that should not have their nested values transformed
// (template settings keys and schema attributes are kept exactly as the API sends them)
const preserveNestedKeys = new Set([
    'release_notes', 'releaseNotes', 'features',
    'settings_schema', 'settingsSchema', 'metadata', 'missing', 'fields',
]);

// Templates are identified by `key`; react-admin needs `id`.
type ApiRecord = Record<string, unknown>;
// Same (untyped) shape transformKeys returns.
type Transformed = ReturnType<typeof transformKeys>;
const withTemplateId = (resource: string, record: Transformed): Transformed =>
    resource === 'templates' && record && record.key ? { ...record, id: record.key } : record;
const withTemplateIds = (resource: string, list: Transformed): Transformed =>
    resource === 'templates' ? list.map((r: ApiRecord) => withTemplateId(resource, r)) : list;

// Transform object keys from camelCase to snake_case
const transformKeysToSnake = (obj: any, preserveValues = false): any => {
    if (preserveValues) return obj;
    if (Array.isArray(obj)) {
        return obj.map(v => transformKeysToSnake(v));
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc: any, key: string) => {
            const snakeKey = toSnakeCase(key);
            const shouldPreserve = preserveNestedKeys.has(key) || preserveNestedKeys.has(snakeKey);
            acc[snakeKey] = shouldPreserve ? obj[key] : transformKeysToSnake(obj[key]);
            return acc;
        }, {});
    }
    return obj;
};

export interface LogtoDataProvider extends DataProvider {
    uploadAndroidKeystore: (clientId: string, data: any) => Promise<any>;
    uploadIOSCertificate: (clientId: string, data: any) => Promise<any>;
    uploadIOSProvisioningProfile: (clientId: string, data: any) => Promise<any>;
    uploadIOSExtensionProfile: (clientId: string, data: any) => Promise<any>;
    uploadGooglePlayServiceAccount: (clientId: string, data: any) => Promise<any>;
    uploadAppStoreConnectKey: (clientId: string, data: any) => Promise<any>;
    uploadImage: (file: File) => Promise<{ url: string }>;
    uploadSplashScreen: (clientId: string, file: File) => Promise<{ splashScreenUrl: string; version: number }>;
    removeSplashScreen: (clientId: string) => Promise<{ version: number }>;
    getMetrics: () => Promise<{ data: Metrics }>;
    // T031: Shared secrets upload methods (Feature 010)
    uploadSharedIOSCertificate: (data: any) => Promise<any>;
    uploadSharedAppStoreConnectKey: (data: any) => Promise<any>;
    uploadSharedGooglePlayServiceAccount: (data: any) => Promise<any>;
    // Build logs
    getBuildLogs: (buildId: string, params?: { limit?: number; offset?: number; level?: string }) => Promise<{ data: any[]; total: number }>;
    // Feature 012: template settings stored separately (secrets/files) and build readiness
    getTemplateSecrets: (clientId: string, templateKey?: string) => Promise<TemplateSecretStatus[]>;
    setTemplateSecret: (clientId: string, key: string, value: string, templateKey?: string) => Promise<TemplateSecretStatus>;
    uploadTemplateFile: (clientId: string, key: string, file: File, templateKey?: string) => Promise<TemplateSecretStatus>;
    deleteTemplateSecret: (clientId: string, key: string, templateKey?: string) => Promise<void>;
    getBuildReadiness: (clientId: string, templateKey?: string) => Promise<BuildReadiness>;
}

export const dataProvider = (
    httpClientFn = fetchUtils.fetchJson,
): LogtoDataProvider => ({
    getList: (resource, params) => {
        const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
        const { field, order } = params.sort || { field: 'id', order: 'ASC' };

        const query = new URLSearchParams({
            limit: perPage.toString(),
            offset: ((page - 1) * perPage).toString(),
            ...(field && { sort: toSnakeCase(field) }),
            ...(order && { order }),
            ...Object.keys(params.filter).reduce((acc: any, key) => {
                const value = params.filter[key];
                if (value !== undefined && value !== null && value !== '') {
                    acc[toSnakeCase(key)] = value;
                }
                return acc;
            }, {}),
        });

        const url = `${apiUrl}/v1/${resource}?${query}`;

        return httpClientFn(url).then(({ json }) => ({
            data: withTemplateIds(resource, transformKeys(json[resource] || [])),
            total: json.total || 0,
        }));
    },

    getOne: (resource, params) => {
        // Special handling for secrets resource
        if (resource === 'secrets') {
            const url = `${apiUrl}/v1/clients/${params.id}/secrets`;
            return httpClientFn(url).then(({ json }) => ({
                data: transformKeys(json),
            }));
        }

        if (resource === 'configurations') {
            const url = `${apiUrl}/v1/clients/${params.id}/configuration`;
            return httpClientFn(url).then(({ json }) => ({
                data: transformKeys(json),
            }));
        }

        const url = `${apiUrl}/v1/${resource}/${params.id}`;
        return httpClientFn(url).then(({ json }) => ({
            data: withTemplateId(resource, transformKeys(json)),
        }));
    },

    getMany: (resource, params) => {
        // Templates are few; the endpoint returns all of them.
        if (resource === 'templates') {
            return httpClientFn(`${apiUrl}/v1/templates`).then(({ json }) => ({
                data: withTemplateIds(resource, transformKeys(json.templates || []))
                    .filter((r: ApiRecord) => params.ids.includes(r.id as string)),
            }));
        }
        const query = new URLSearchParams({
            ids: params.ids.join(','),
        });
        const url = `${apiUrl}/v1/${resource}?${query}`;
        return httpClientFn(url).then(({ json }) => ({
            data: transformKeys(json[resource] || []),
        }));
    },

    getManyReference: (resource, params) => {
        const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
        const { field, order } = params.sort || { field: 'id', order: 'ASC' };

        const query = new URLSearchParams({
            limit: perPage.toString(),
            offset: ((page - 1) * perPage).toString(),
            ...(field && { sort: toSnakeCase(field) }),
            ...(order && { order }),
            [toSnakeCase(params.target)]: params.id,
            ...Object.keys(params.filter).reduce((acc: any, key) => {
                acc[toSnakeCase(key)] = params.filter[key];
                return acc;
            }, {}),
        });

        const url = `${apiUrl}/v1/${resource}?${query}`;

        return httpClientFn(url).then(({ json }) => ({
            data: transformKeys(json[resource] || []),
            total: json.total || 0,
        }));
    },

    create: (resource, params) => {
        // Handle file uploads
        if (params.data.file?.rawFile || params.data.files) {
            const formData = new FormData();
            Object.keys(params.data).forEach((key) => {
                const value = params.data[key];
                if (value?.rawFile) {
                    formData.append(toSnakeCase(key), value.rawFile);
                } else if (value !== undefined && value !== null) {
                    formData.append(toSnakeCase(key), value.toString());
                }
            });

            const headers = new Headers([["Content-Type", "multipart/form-data"]]);

            return httpClientFn(`${apiUrl}/v1/clients/${params.data.clientId}/secrets`, {
                method: 'PUT',
                body: formData,
                headers,
            }).then(({ json }) => ({ data: transformKeys(json) }));
        }

        // Mass rebuild routes to a dedicated endpoint.
        if (resource === 'builds' && (params.data as any).triggerType === 'mass_rebuild') {
            const data = params.data as any;
            const body: Record<string, unknown> = {
                client_ids: data.clientIds ?? [],
                skip_publish: data.skipPublish ?? false,
                publish_android: data.publishAndroid ?? true,
                publish_ios: data.publishIos ?? true,
                testing_only: data.testingOnly ?? false,
                environment: data.environment || 'prod',
            };
            if (data.templateKey) body.template_key = data.templateKey;
            if (data.coreVersion) body.core_version = data.coreVersion;
            const releaseNotes = data.releaseNotes;
            if (releaseNotes && Object.keys(releaseNotes).length > 0) {
                body.release_notes = releaseNotes;
            }
            return httpClientFn(`${apiUrl}/v1/builds/mass`, {
                method: 'POST',
                body: JSON.stringify(body),
            }).then(({ json }) => {
                const transformed: any = transformKeys(json);
                return { data: { id: transformed.buildIds?.[0] ?? 'mass', ...transformed } };
            });
        }

        return httpClientFn(`${apiUrl}/v1/${resource}`, {
            method: 'POST',
            body: JSON.stringify(transformKeysToSnake(params.data)),
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    update: (resource, params) => {
        // Special handling for configurations - nested resource under clients
        if (resource === 'configurations') {
            const url = `${apiUrl}/v1/clients/${params.id}/configuration`;
            return httpClientFn(url, {
                method: 'PUT',
                body: JSON.stringify(transformKeysToSnake(params.data)),
            }).then(({ json }) => ({ data: transformKeys(json) }));
        }

        // Special handling for secrets - nested resource under clients (Feature: 008-add-team-name)
        if (resource === 'secrets') {
            const url = `${apiUrl}/v1/clients/${params.id}/secrets`;
            return httpClientFn(url, {
                method: 'PATCH',
                body: JSON.stringify(transformKeysToSnake(params.data)),
            }).then(({ json }) => ({ data: transformKeys(json) }));
        }

        // Templates: send only the editable fields (the API rejects unknown ones).
        if (resource === 'templates') {
            const { name, repoUrl, sourceMode, sourceRef, active } = params.data as ApiRecord;
            return httpClientFn(`${apiUrl}/v1/templates/${params.id}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    name,
                    repo_url: repoUrl ?? '',
                    source_mode: sourceMode,
                    source_ref: sourceRef,
                    active,
                }),
            }).then(({ json }) => ({ data: withTemplateId(resource, transformKeys(json)) }));
        }

        // Standard update for other resources
        const url = `${apiUrl}/v1/${resource}/${params.id}`;
        return httpClientFn(url, {
            method: 'PATCH',
            body: JSON.stringify(transformKeysToSnake(params.data)),
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    updateMany: (resource, params) => {
        return Promise.all(
            params.ids.map((id) =>
                httpClientFn(`${apiUrl}/v1/${resource}/${id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(transformKeysToSnake(params.data)),
                })
            )
        ).then((responses) => ({
            data: responses.map((response) => response.json.id),
        }));
    },

    delete: (resource, params) => {
        const url = `${apiUrl}/v1/${resource}/${params.id}`;
        return httpClientFn(url, {
            method: 'DELETE',
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    deleteMany: (resource, params) => {
        return Promise.all(
            params.ids.map((id) =>
                httpClientFn(`${apiUrl}/v1/${resource}/${id}`, {
                    method: 'DELETE',
                })
            )
        ).then((responses) => ({
            data: responses.map((response) => response.json.id),
        }));
    },

    // New methods for secrets management
    uploadAndroidKeystore: async (clientId: string, data: any) => {

        const formData = new FormData();
        formData.append('keystore', data.keystore);
        formData.append('keystore_password', data.keystore_password);
        formData.append('key_alias', data.key_alias);
        formData.append('key_password', data.key_password);

        // const headers = new Headers([["Content-Type", "multipart/form-data"]]);

        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/secrets/android`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    uploadIOSCertificate: async (clientId: string, data: any) => {
        const formData = new FormData();
        formData.append('certificate', data.certificate);
        formData.append('certificate_password', data.certificate_password);

        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/secrets/ios`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    uploadIOSProvisioningProfile: async (clientId: string, data: any) => {
        const formData = new FormData();
        formData.append('provisioning_profile', data.provisioning_profile);

        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/secrets/ios/provisioning-profile`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    uploadIOSExtensionProfile: async (clientId: string, data: any) => {
        const formData = new FormData();
        formData.append('extension_profile', data.extension_profile);

        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/secrets/ios/extension-profile`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    uploadGooglePlayServiceAccount: async (clientId: string, data: any) => {

        const formData = new FormData();
        formData.append('service_account', data.service_account);

        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/secrets/google-play`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));

    },

    uploadAppStoreConnectKey: async (clientId: string, data: any) => {

        const formData = new FormData();
        formData.append('key_file', data.key_file);
        formData.append('key_id', data.key_id);
        formData.append('issuer_id', data.issuer_id);

        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/secrets/app-store`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    uploadImage: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        return httpClientFn(`${apiUrl}/v1/upload/image`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => transformKeys(json));
    },

    uploadSplashScreen: async (clientId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/configuration/splash-screen`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => transformKeys(json));
    },

    removeSplashScreen: async (clientId: string) => {
        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/configuration/splash-screen`, {
            method: 'DELETE',
        }).then(({ json }) => transformKeys(json));
    },

    getMetrics: async () => {
        const url = `${apiUrl}/v1/metrics`;
        return httpClientFn(url).then(({ json }) => ({ data: transformKeys(json) }));
    },

    // T031: Shared secrets upload methods (Feature 010)
    uploadSharedIOSCertificate: async (data: any) => {
        const formData = new FormData();
        formData.append('certificate', data.certificate);
        formData.append('certificate_password', data.certificate_password);

        // Optional metadata
        if (data.certificate_id) formData.append('certificate_id', data.certificate_id);
        if (data.certificate_expiration) formData.append('certificate_expiration', data.certificate_expiration);

        return httpClientFn(`${apiUrl}/v1/shared-secrets/ios/certificate`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    uploadSharedAppStoreConnectKey: async (data: any) => {
        const formData = new FormData();
        formData.append('key', data.key);
        formData.append('key_id', data.key_id);
        formData.append('issuer_id', data.issuer_id);

        return httpClientFn(`${apiUrl}/v1/shared-secrets/ios/app-store-connect-key`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    uploadSharedGooglePlayServiceAccount: async (data: any) => {
        const formData = new FormData();
        formData.append('service_account', data.service_account);

        return httpClientFn(`${apiUrl}/v1/shared-secrets/android/google-play-service-account`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => ({ data: transformKeys(json) }));
    },

    getBuildLogs: async (buildId: string, params?: { limit?: number; offset?: number; level?: string }) => {
        const query = new URLSearchParams({
            limit: (params?.limit || 100).toString(),
            offset: (params?.offset || 0).toString(),
            ...(params?.level && { level: params.level }),
        });

        return httpClientFn(`${apiUrl}/v1/builds/${buildId}/logs?${query}`).then(({ json }) => ({
            data: transformKeys(json.logs || []),
            total: json.total || 0,
        }));
    },

    // Feature 012: template secrets / files and build readiness
    getTemplateSecrets: async (clientId: string, templateKey?: string) => {
        const query = templateKey ? `?template_key=${encodeURIComponent(templateKey)}` : '';
        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/template-secrets${query}`)
            .then(({ json }) => transformKeys(json.data || []));
    },

    setTemplateSecret: async (clientId: string, key: string, value: string, templateKey?: string) => {
        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/template-secrets/${encodeURIComponent(key)}`, {
            method: 'PUT',
            body: JSON.stringify({ value, ...(templateKey && { template_key: templateKey }) }),
        }).then(({ json }) => transformKeys(json));
    },

    uploadTemplateFile: async (clientId: string, key: string, file: File, templateKey?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        if (templateKey) formData.append('template_key', templateKey);
        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/template-secrets/${encodeURIComponent(key)}`, {
            method: 'POST',
            body: formData,
        }).then(({ json }) => transformKeys(json));
    },

    deleteTemplateSecret: async (clientId: string, key: string, templateKey?: string) => {
        const query = templateKey ? `?template_key=${encodeURIComponent(templateKey)}` : '';
        await httpClientFn(`${apiUrl}/v1/clients/${clientId}/template-secrets/${encodeURIComponent(key)}${query}`, {
            method: 'DELETE',
        });
    },

    getBuildReadiness: async (clientId: string, templateKey?: string) => {
        const query = templateKey ? `?template_key=${encodeURIComponent(templateKey)}` : '';
        return httpClientFn(`${apiUrl}/v1/clients/${clientId}/build-readiness${query}`)
            .then(({ json }) => transformKeys(json));
    },
});
